/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  Image,
  Text,
  Heading,
  Badge,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  VStack,
  useToast,
  useColorModeValue,
  Icon,
  SimpleGrid,
  Divider,
  HStack,
  IconButton,
  ScaleFade,
  Tooltip,
  Progress,
  Skeleton,
  SkeletonText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalCloseButton,
  ButtonGroup,
  useDisclosure as useChakraDisclosure,
  useBreakpointValue,
  Spinner,
} from '@chakra-ui/react';
import { FaShoppingCart, FaTrash, FaCheck, FaPlus, FaMinus, FaInfoCircle, FaList } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { CitySelect, CountySelect } from 'components';
import { FormControlFormInput, FormControlFormPhoneInput, GenericMessages } from 'core';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Api } from '../api/api';
import { EStoreProduct } from '../types/util.types';
import { ExpertisePoint } from '../types/expertise-point.types';
import { useAuthUser } from '../core/hooks';
import { SaveOrderRequest } from '../types/order.types';

// Motion components for animations
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// Placeholder görsel, eğer product.imageUrl tanımsızsa kullanılır
const FALLBACK_IMAGE_URL = 'https://placehold.co/600x400?text=Hazırlanıyor';

interface ProductVariant {
  id: number;
  variantName: string;
  variantValue: string;
  stock?: number;
}

interface Product {
  id: number;
  name: string;
  price: number | null;
  imageUrl?: string;
  photoUrls?: string[];
  variants?: ProductVariant[];
  productType: number;
  description?: string;
  title?: string;
}

interface CartItem extends Product {
  quantity: number;
  selectedVariantId?: number;
}

// Form validation schema for checkout information
const checkoutFormValidation = yup.object({
  name: yup.string().required(GenericMessages.CannotBeEmpty),
  phone: yup.string().required(GenericMessages.CannotBeEmpty),
  address: yup.string().required(GenericMessages.CannotBeEmpty),
  cityId: yup.string().required(GenericMessages.CannotBeEmpty),
  countyId: yup.string().required(GenericMessages.CannotBeEmpty),
});

// Extracted ProductCard component
const ProductCard = React.memo(
  ({
    product,
    onOpenModal,
    cartItems,
    cardBgColor,
    borderColor,
    textColorPrimary,
    priceColor,
    primaryActionScheme,
    highlightColorScheme,
  }: {
    product: Product;
    onOpenModal: (product: Product) => void;
    cartItems: CartItem[];
    cardBgColor: string;
    borderColor: string;
    textColorPrimary: string;
    priceColor: string;
    primaryActionScheme: string;
    highlightColorScheme: string;
  }) => {
    const cartItem = useMemo(() => cartItems.find((item) => item.id === product.id), [cartItems, product.id]);

    return (
      <MotionBox
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        overflow="hidden"
        boxShadow="lg"
        bg={cardBgColor}
        transition={{ duration: 0.2 }}
        whileHover={{
          y: -6,
          boxShadow: '0 25px 30px -10px rgba(0, 0, 0, 0.15), 0 10px 15px -5px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box position="relative">
          <Image
            src={product.photoUrls?.[0] || FALLBACK_IMAGE_URL}
            alt={product.name}
            w="100%"
            h="220px"
            objectFit="cover"
            fallbackSrc={FALLBACK_IMAGE_URL}
          />
          {cartItem && (
            <Badge
              position="absolute"
              top="10px"
              right="10px"
              borderRadius="full"
              px="3"
              py="1.5"
              fontSize="0.9em"
              fontWeight="bold"
              boxShadow="md"
              bg={useColorModeValue(`${highlightColorScheme}.500`, `${highlightColorScheme}.400`)}
              color="white"
              variant="solid"
            >
              {cartItem.quantity}
            </Badge>
          )}
        </Box>
        <Box p={5}>
          <Heading
            as="h3"
            size="md"
            mb={1}
            noOfLines={2}
            title={product.name}
            color={textColorPrimary}
            fontWeight="semibold"
            minHeight="48px"
          >
            {product.name}
          </Heading>
          {product.price !== null && (
            <Text color={priceColor} fontWeight="bold" fontSize="xl" mb={4}>
              {product.price.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
            </Text>
          )}
          <Button
            colorScheme={primaryActionScheme}
            w="100%"
            leftIcon={<Icon as={FaShoppingCart} />}
            onClick={() => onOpenModal(product)}
            boxShadow="sm"
            _hover={{ boxShadow: 'md' }}
          >
            Sepete Ekle
          </Button>
        </Box>
      </MotionBox>
    );
  },
);

// Extracted CartItem component
const CartItemComponent = React.memo(
  ({
    item,
    onUpdateQuantity,
    onRemove,
    cardBgColor,
    borderColor,
    textColorPrimary,
    textColorSecondary,
    priceColor,
    primaryActionScheme,
  }: {
    item: CartItem;
    onUpdateQuantity: (id: number, quantity: number, variantId?: number) => void;
    onRemove: (id: number) => void;
    cardBgColor: string;
    borderColor: string;
    textColorPrimary: string;
    textColorSecondary: string;
    priceColor: string;
    primaryActionScheme: string;
  }) => {
    return (
      <ScaleFade initialScale={0.9} in>
        <MotionFlex
          align="center"
          p={3}
          borderWidth="1px"
          borderRadius="md"
          borderColor={borderColor}
          bg={cardBgColor}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          layout
        >
          <Image
            src={item.photoUrls?.[0] || FALLBACK_IMAGE_URL}
            alt={item.name}
            boxSize="60px"
            objectFit="cover"
            borderRadius="md"
            mr={3}
            fallbackSrc={FALLBACK_IMAGE_URL}
          />
          <Box flex="1">
            <Text fontWeight="bold" noOfLines={1} color={textColorPrimary}>
              {item.name}
            </Text>
            {item.selectedVariantId && item.variants && (
              <Text fontSize="sm" color={textColorSecondary}>
                {item.variants.find((v) => v.id === item.selectedVariantId)?.variantName}:{' '}
                {item.variants.find((v) => v.id === item.selectedVariantId)?.variantValue}
              </Text>
            )}
            {item.price !== null && (
              <Text fontSize="sm" color={priceColor} fontWeight="semibold">
                {item.price.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
              </Text>
            )}
            {item.price !== null && (
              <Text fontSize="xs" color={textColorSecondary}>
                Toplam: {(item.price * item.quantity).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
              </Text>
            )}
          </Box>
          <HStack>
            <Flex align="center">
              <IconButton
                aria-label="Azalt"
                icon={<FaMinus />}
                size="xs"
                isRound
                colorScheme={primaryActionScheme}
                variant="outline"
                onClick={() => onUpdateQuantity(item.id, item.quantity - 1, item.selectedVariantId)}
              />
              <Text mx={2} fontWeight="bold" minW="30px" textAlign="center">
                {item.quantity}
              </Text>
              <IconButton
                aria-label="Artır"
                icon={<FaPlus />}
                size="xs"
                isRound
                colorScheme={primaryActionScheme}
                variant="outline"
                onClick={() => onUpdateQuantity(item.id, item.quantity + 1, item.selectedVariantId)}
                isDisabled={
                  item.selectedVariantId
                    ? item.variants?.find((v) => v.id === item.selectedVariantId)?.stock === item.quantity
                    : false
                }
              />
            </Flex>
            <IconButton
              aria-label="Kaldır"
              icon={<FaTrash />}
              size="sm"
              colorScheme="red"
              variant="ghost"
              onClick={() => onRemove(item.id)}
            />
          </HStack>
        </MotionFlex>
      </ScaleFade>
    );
  },
);

// Extracted ProductModal component
const ProductModal = React.memo(
  ({
    isOpen,
    onClose,
    product,
    selectedVariantId,
    setSelectedVariantId,
    modalQuantity,
    setModalQuantity,
    onAddToCart,
    cardBgColor,
    textColorPrimary,
    priceColor,
    borderColor,
    highlightColorScheme,
    bgColor,
  }: {
    isOpen: boolean;
    onClose: () => void;
    product: Product | null;
    selectedVariantId: number | null;
    setSelectedVariantId: (id: number | null) => void;
    modalQuantity: number;
    setModalQuantity: (quantity: number) => void;
    onAddToCart: () => void;
    cardBgColor: string;
    textColorPrimary: string;
    priceColor: string;
    borderColor: string;
    highlightColorScheme: string;
    bgColor: string;
  }) => {
    const [isImageLoading, setIsImageLoading] = useState(true);
    const isMobile = useBreakpointValue({ base: true, md: false });

    // Query for product details if needed
    const { isLoading: isLoadingProductDetails } = useQuery(
      ['productDetails', product?.id],
      async () => {
        if (!product?.id) return null;
        return product;
      },
      {
        enabled: !!product?.id,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      },
    );

    const handleQuantityChange = useCallback(
      (newQuantity: number) => {
        let maxQuantity = newQuantity;
        if (product?.variants && selectedVariantId) {
          const variant = product.variants.find((v) => v.id === selectedVariantId);
          if (variant?.stock !== undefined) {
            maxQuantity = Math.min(newQuantity, variant.stock);
          }
        }
        setModalQuantity(Math.max(1, maxQuantity));
      },
      [product?.variants, selectedVariantId, setModalQuantity],
    );

    if (!product) return null;

    return (
      <Modal isOpen={isOpen} onClose={onClose} size={isMobile ? 'full' : '4xl'} isCentered motionPreset="none">
        <ModalOverlay />
        <ModalContent bg={bgColor} color={textColorPrimary} borderRadius="2xl" boxShadow="2xl" p={0} maxW="900px">
          <ModalCloseButton color={textColorPrimary} top={4} right={4} size="lg" />
          <Flex direction={{ base: 'column', md: 'row' }}>
            <Box
              flex="2"
              minW="400px"
              maxW="600px"
              p={10}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg={cardBgColor}
              borderRadius="xl"
              overflow="hidden"
              position="relative"
            >
              {isLoadingProductDetails ? (
                <Skeleton height="400px" width="600px" />
              ) : (
                <>
                  {isImageLoading && (
                    <Flex position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={1}>
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color={useColorModeValue('orange.500', 'orange.300')}
                        size="xl"
                      />
                    </Flex>
                  )}
                  <Image
                    src={product.photoUrls?.[0] || ''}
                    alt={product.name}
                    borderRadius="xl"
                    objectFit="contain"
                    w="600px"
                    h="400px"
                    opacity={isImageLoading ? 0 : 1}
                    transition="opacity 0.2s"
                    onLoad={() => setIsImageLoading(false)}
                    onError={() => setIsImageLoading(false)}
                  />
                </>
              )}
            </Box>
            <Box flex="1" p={{ base: 6, md: 12 }}>
              <ModalHeader p={0} mb={6} color={textColorPrimary} fontWeight="bold" fontSize="2xl" letterSpacing="tight">
                {isLoadingProductDetails ? <SkeletonText noOfLines={2} spacing="4" /> : product.name}
              </ModalHeader>
              {product.price !== null && (
                <Text fontSize="2xl" fontWeight="bold" color={priceColor} mb={6}>
                  {isLoadingProductDetails ? (
                    <Skeleton height="32px" width="120px" />
                  ) : (
                    product.price.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })
                  )}
                </Text>
              )}
              {product.variants && product.variants.length > 0 && (
                <Box mb={6}>
                  <Text fontWeight="bold" mb={2} color={textColorPrimary}>
                    {product.variants[0]?.variantName} Seç:
                  </Text>
                  {isLoadingProductDetails ? (
                    <Skeleton height="40px" />
                  ) : (
                    <ButtonGroup isAttached variant="outline" size="sm">
                      {product.variants.map((variantOption) => (
                        <Button
                          key={variantOption.id}
                          onClick={() => setSelectedVariantId(variantOption.id)}
                          colorScheme={selectedVariantId === variantOption.id ? highlightColorScheme : 'gray'}
                          bg={
                            selectedVariantId === variantOption.id
                              ? useColorModeValue('orange.300', 'orange.400')
                              : 'transparent'
                          }
                          color={
                            selectedVariantId === variantOption.id
                              ? useColorModeValue('#181818', 'white')
                              : textColorPrimary
                          }
                          borderColor={
                            selectedVariantId === variantOption.id
                              ? useColorModeValue('orange.300', 'orange.400')
                              : borderColor
                          }
                          fontWeight="bold"
                          fontSize="md"
                          mx={1}
                          isDisabled={variantOption.stock === 0}
                          _hover={{
                            bg:
                              selectedVariantId === variantOption.id
                                ? useColorModeValue('orange.200', 'orange.500')
                                : useColorModeValue('gray.100', 'gray.700'),
                          }}
                        >
                          {variantOption.variantValue}
                        </Button>
                      ))}
                    </ButtonGroup>
                  )}
                </Box>
              )}
              <Box mb={6}>
                <Text fontWeight="bold" mb={2} color={textColorPrimary}>
                  {product.variants?.[0]?.variantName} Seç:
                </Text>
                {isLoadingProductDetails ? (
                  <Skeleton height="40px" />
                ) : (
                  <Flex align="center" gap={4}>
                    <IconButton
                      aria-label="Azalt"
                      icon={<FaMinus />}
                      size="md"
                      onClick={() => handleQuantityChange(modalQuantity - 1)}
                      isDisabled={modalQuantity <= 1 || (product.variants && !selectedVariantId)}
                      colorScheme={highlightColorScheme}
                      variant="outline"
                    />
                    <Text fontSize="xl" fontWeight="bold" w="40px" textAlign="center">
                      {modalQuantity}
                    </Text>
                    <IconButton
                      aria-label="Artır"
                      icon={<FaPlus />}
                      size="md"
                      onClick={() => handleQuantityChange(modalQuantity + 1)}
                      colorScheme={highlightColorScheme}
                      variant="outline"
                      isDisabled={
                        (product.variants && !selectedVariantId) ||
                        (product.variants &&
                          selectedVariantId &&
                          product.variants.find((v) => v.id === selectedVariantId)?.stock === modalQuantity) ||
                        false
                      }
                    />
                  </Flex>
                )}
                {product.variants && selectedVariantId && (
                  <Box mt={2}>
                    {(() => {
                      const variant = product.variants?.find((v) => v.id === selectedVariantId);
                      if (variant && typeof variant.stock === 'number') {
                        return (
                          <Text fontSize="sm" color={variant.stock === 0 ? 'red.400' : 'green.300'} fontWeight="bold">
                            Stok: {variant.stock}
                          </Text>
                        );
                      }
                      return null;
                    })()}
                  </Box>
                )}
              </Box>
              {product.description && (
                <Box mb={6}>
                  <Text fontWeight="bold" color={priceColor} mb={1}>
                    Açıklama
                  </Text>
                  {isLoadingProductDetails ? (
                    <SkeletonText noOfLines={3} spacing="4" />
                  ) : (
                    <Text color={textColorPrimary}>{product.description}</Text>
                  )}
                </Box>
              )}
              <ModalFooter p={0} mt={8}>
                <Button
                  colorScheme={highlightColorScheme}
                  bg={useColorModeValue('orange.300', 'orange.400')}
                  color={useColorModeValue('#181818', 'white')}
                  size="lg"
                  fontWeight="bold"
                  w="full"
                  borderRadius="md"
                  onClick={onAddToCart}
                  isDisabled={
                    isLoadingProductDetails ||
                    !!(
                      (product.variants && !selectedVariantId) ||
                      (product.variants &&
                        selectedVariantId &&
                        product.variants.find((v) => v.id === selectedVariantId)?.stock === 0)
                    )
                  }
                  _hover={{ bg: useColorModeValue('orange.200', 'orange.500') }}
                >
                  {isLoadingProductDetails ? <Skeleton height="40px" width="full" /> : 'SEPETE EKLE'}
                </Button>
              </ModalFooter>
            </Box>
          </Flex>
        </ModalContent>
      </Modal>
    );
  },
);

export const ProductShop: React.FC = () => {
  const authUser = useAuthUser();
  const [cart, setCart] = useState<CartItem[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [modalQuantity, setModalQuantity] = useState(1);
  const {
    isOpen: isProductModalOpen,
    onOpen: onProductModalOpen,
    onClose: onProductModalClose,
  } = useChakraDisclosure();

  // Renk Paleti ve Stil Ayarları
  const bgColor = useColorModeValue('gray.100', '#0B192C');
  const cardBgColor = useColorModeValue('white', '#1E3E62');
  const borderColor = useColorModeValue('gray.200', '#4A5568');
  const textColorPrimary = useColorModeValue('gray.800', 'whiteAlpha.900');
  const textColorSecondary = useColorModeValue('gray.600', 'gray.400');
  const subtleColor = useColorModeValue('gray.100', '#1E3E62');
  const priceColor = useColorModeValue('orange.300', '#FF6500');
  const primaryActionScheme = 'orange';
  const cartButtonScheme = 'blue';
  const highlightColorScheme = 'orange';

  // 1. Ürünleri Çekme (useQuery)
  const {
    data: productsData,
    isLoading: isLoadingProducts,
    error: productsError,
  } = useQuery<EStoreProduct[], Error>(
    'eStoreProducts',
    async () => {
      const response = await Api.Product.getCanBeOrderedProducts();
      return response.data || [];
    },
    {
      onError: () => {
        toast({
          title: 'Hata',
          description: 'Ürünler yüklenirken bir sorun oluştu.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );

  const products: Product[] = React.useMemo(
    () =>
      (productsData || []).map((item) => ({
        id: item.id,
        name: item.name,
        price: item.price,
        imageUrl: item.photoUrls?.[0],
        photoUrls: item.photoUrls,
        variants: item.variants,
        productType: item.productType,
        description: item.description,
        title: item.title,
      })),
    [productsData],
  );

  // 2. Teslimat Bilgilerini Çekme (useQuery)
  const expertisePointId = authUser?.expertisePointId;
  const {
    data: expertisePointData,
    isLoading: isLoadingCheckoutInfo,
    error: expertisePointError,
  } = useQuery<ExpertisePoint, Error>(
    ['expertisePoint', expertisePointId],
    async () => {
      if (!expertisePointId) throw new Error('Expertise point ID is not available.');
      const response = await Api.ExpertisePoint.getExpertisePointById(expertisePointId);
      return response.data.expertisePoint;
    },
    {
      enabled: !!expertisePointId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onError: () => {
        toast({
          title: 'Teslimat Bilgileri Yüklenemedi',
          description: 'Bayi bilgileri çekilirken bir sorun oluştu.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );

  // 3. Bakiye Bilgisini Çekme (useQuery)
  const {
    data: balanceData,
    isLoading: isLoadingBalance,
    error: balanceError,
  } = useQuery<{ totalBalance: number }, Error>(
    ['expertisePointBalance'],
    async () => {
      const response = await Api.ExpertisePoint.getTransactions();
      return response.data;
    },
    {
      enabled: !!authUser,
      onError: () => {
        toast({
          title: 'Bakiye Yüklenemedi',
          description: 'Güncel bakiye bilgisi çekilirken bir sorun oluştu.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
      initialData: { totalBalance: 0 },
    },
  );

  const currentBalance = balanceData?.totalBalance ?? 0;

  // Memoized values
  const total = useMemo(() => cart.reduce((sum, item) => sum + (item.price ?? 0) * item.quantity, 0), [cart]);
  const totalItems = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);
  const balanceAfterPurchase = useMemo(() => currentBalance - total, [currentBalance, total]);
  const balancePercentage = useMemo(
    () => (currentBalance > 0 ? Math.max(0, Math.min(100, (balanceAfterPurchase / currentBalance) * 100)) : 0),
    [currentBalance, balanceAfterPurchase],
  );

  const formik = useFormik({
    initialValues: {
      name: `${authUser?.name} ${authUser?.surname}` || '',
      phone: expertisePointData?.mobilePhoneNumber?.toString() || '',
      address: expertisePointData?.address || '',
      cityId: expertisePointData?.cityId?.toString() || '',
      countyId: expertisePointData?.countyId?.toString() || '',
    },
    validationSchema: checkoutFormValidation,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      const orderPayload: SaveOrderRequest = {
        items: cart.map((item) => ({
          productId: item.id,
          quantity: item.quantity,
          variantId: item.selectedVariantId,
        })),
        shippingAddress: {
          fullName: values.name,
          address: values.address,
          cityId: parseInt(values.cityId, 10),
          countyId: parseInt(values.countyId, 10),
          phoneNumber: values.phone,
        },
      };

      try {
        await Api.Order.saveOrder(orderPayload);

        // Bakiye ve ürün bilgilerini güncelle
        queryClient.invalidateQueries(['expertisePointBalance']);
        queryClient.invalidateQueries(['eStoreProducts']);
        queryClient.invalidateQueries(['productDetails']);

        setCart([]);
        onClose();
        toast({
          title: 'Siparişiniz alındı',
          description: 'Siparişiniz başarıyla oluşturuldu.',
          status: 'success',
          position: 'top',
          duration: 3000,
          isClosable: true,
          icon: <Icon as={FaCheck} />,
        });
      } catch (error) {
        console.error('Error saving order:', error);
        toast({
          title: 'Sipariş Kaydedilemedi',
          description: 'Siparişiniz oluşturulurken bir hata oluştu.',
          status: 'error',
          position: 'top',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (expertisePointData) {
      formik.setValues({
        name: `${authUser?.name} ${authUser?.surname}` || '',
        phone: expertisePointData.mobilePhoneNumber?.toString() || '',
        address: expertisePointData.address || '',
        cityId: expertisePointData.cityId?.toString() || '',
        countyId: expertisePointData.countyId?.toString() || '',
      });
    }
  }, [expertisePointData, authUser]);

  useEffect(() => {
    if (productsError) console.error('Error fetching products:', productsError);
    if (expertisePointError) console.error('Error fetching expertise point:', expertisePointError);
    if (balanceError) console.error('Error fetching balance:', balanceError);
  }, [productsError, expertisePointError, balanceError]);

  // Memoized callbacks
  const handleOpenProductModal = useCallback(
    (product: Product) => {
      setSelectedProduct(product);
      setSelectedVariantId(null);
      setModalQuantity(1);
      onProductModalOpen();
    },
    [onProductModalOpen],
  );

  const removeFromCart = useCallback(
    (productId: number) => {
      setCart((prev) => prev.filter((item) => item.id !== productId));
      toast({
        title: 'Ürün sepetten çıkarıldı',
        status: 'info',
        duration: 1000,
        position: 'top-right',
        isClosable: true,
      });
    },
    [toast],
  );

  const updateCartItemQuantity = useCallback(
    (productId: number, newQuantity: number, variantId?: number) => {
      if (newQuantity < 1) {
        removeFromCart(productId);
        return;
      }

      const cartItem = cart.find((item) => item.id === productId);
      if (cartItem && variantId) {
        const variant = cartItem.variants?.find((v) => v.id === variantId);
        if (variant?.stock !== undefined && newQuantity > variant.stock) {
          toast({
            title: 'Stok Hatası',
            description: 'Maksimum stok miktarına ulaştınız.',
            status: 'warning',
            duration: 2000,
            position: 'top-right',
            isClosable: true,
          });
          return;
        }
      }

      setCart((prev) => prev.map((item) => (item.id === productId ? { ...item, quantity: newQuantity } : item)));
    },
    [cart, removeFromCart, toast],
  );

  // Add mutation for adding to cart
  const addToCartMutation = useMutation(
    async (cartData: { product: Product; quantity: number; variantId?: number }) => {
      // Simulate API call or add actual API call here
      await new Promise((resolve) => {
        setTimeout(resolve, 500);
      });
      return cartData;
    },
    {
      onSuccess: (cartData) => {
        setCart((prev) => {
          const found = prev.find(
            (item) =>
              item.id === cartData.product.id &&
              (cartData.product.variants ? item.selectedVariantId === cartData.variantId : true),
          );
          if (found) {
            return prev.map((item) =>
              item.id === cartData.product.id && item.selectedVariantId === cartData.variantId
                ? { ...item, quantity: item.quantity + cartData.quantity }
                : item,
            );
          }
          return [
            ...prev,
            {
              ...cartData.product,
              quantity: cartData.quantity,
              selectedVariantId: cartData.variantId ?? undefined,
            },
          ];
        });
        toast({
          title: 'Ürün sepete eklendi',
          status: 'success',
          duration: 1000,
          position: 'top-right',
          isClosable: true,
        });
        onProductModalClose();
      },
      onError: () => {
        toast({
          title: 'Hata',
          description: 'Ürün sepete eklenirken bir hata oluştu.',
          status: 'error',
          duration: 2000,
          position: 'top-right',
          isClosable: true,
        });
      },
    },
  );

  const handleAddToCartFromModal = useCallback(() => {
    if (!selectedProduct) return;

    let selectedVariant: ProductVariant | undefined;
    if (selectedProduct.variants && selectedVariantId) {
      selectedVariant = selectedProduct.variants.find((v) => v.id === selectedVariantId);
      if (!selectedVariant || selectedVariant.stock === 0) {
        toast({
          title: 'Stok Hatası',
          description: 'Bu ürün stokta bulunmamaktadır.',
          status: 'error',
          duration: 2000,
          position: 'top-right',
          isClosable: true,
        });
        return;
      }
      if (selectedVariant.stock !== undefined && modalQuantity > selectedVariant.stock) {
        toast({
          title: 'Stok Hatası',
          description: 'Maksimum stok miktarına ulaştınız.',
          status: 'warning',
          duration: 2000,
          position: 'top-right',
          isClosable: true,
        });
        return;
      }
    }

    addToCartMutation.mutate({
      product: selectedProduct,
      quantity: modalQuantity,
      variantId: selectedVariantId ?? undefined,
    });
  }, [selectedProduct, selectedVariantId, modalQuantity, toast, addToCartMutation]);

  const handleCheckout = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      formik.handleSubmit();
    },
    [formik],
  );

  return (
    <Box p={[4, 6, 8]} bg={bgColor} minH="100vh">
      {/* Başlık Alanı */}
      <Flex justify="center" align="center" mb={4}>
        <Heading
          size="xl"
          color={textColorPrimary}
          pb={2}
          borderBottomWidth="2px"
          borderColor={useColorModeValue(`${primaryActionScheme}.200`, priceColor)}
          letterSpacing="tight"
        >
          E-Mağaza
        </Heading>
      </Flex>

      {/* Geri Butonu ve Sepet Butonu Alanı */}
      <Flex justify="space-between" align="flex-start" mb={8}>
        <VStack spacing={4} align="start">
          <Tooltip label="Ana Sayfa" placement="bottom" hasArrow>
            <Button
              leftIcon={<ChevronLeftIcon boxSize={8} />}
              onClick={() => navigate('/')}
              variant="ghost"
              bg="transparent"
              color={useColorModeValue(`${cartButtonScheme}.400`, textColorPrimary)}
              size="lg"
              borderRadius="full"
              transition="all 0.2s ease-in-out"
            >
              Ana Sayfa
            </Button>
          </Tooltip>
          <Button
            onClick={() => navigate('/orders')}
            colorScheme={cartButtonScheme}
            variant="outline"
            size="lg"
            leftIcon={<Icon as={FaList} />}
          >
            {authUser?.isAdmin || authUser?.isSuperAdmin ? 'Siparişler' : 'Siparişlerim'}
          </Button>
          {(authUser?.isAdmin || authUser?.isSuperAdmin) && (
            <Button
              onClick={() => navigate('/admin/e-store')}
              colorScheme="orange"
              variant="outline"
              size="lg"
              leftIcon={<Icon as={FaInfoCircle} />}
            >
              Ürün Yönetimi
            </Button>
          )}
        </VStack>

        <Box width={['full', 'auto']}>
          <Button
            onClick={onOpen}
            colorScheme={useColorModeValue(cartButtonScheme, 'orange')}
            variant="solid"
            size="lg"
            position="relative"
            width="full"
            height="50px"
            fontSize="md"
            fontWeight="bold"
            leftIcon={<Icon as={FaShoppingCart} boxSize="1.2em" />}
            boxShadow="md"
            rightIcon={
              totalItems > 0 ? (
                <Badge colorScheme={highlightColorScheme} variant="solid" fontSize="sm" py={1} px={2} borderRadius="md">
                  {total.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                </Badge>
              ) : undefined
            }
          >
            Sepetim
            {totalItems > 0 && (
              <Badge
                position="absolute"
                top="-8px"
                left="-8px"
                borderRadius="full"
                boxSize={7}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="sm"
                fontWeight="bold"
                border={`2px solid ${cardBgColor}`}
                transform="scale(1.1)"
                bg={useColorModeValue(`${highlightColorScheme}.500`, `${highlightColorScheme}.400`)}
                color="white"
                boxShadow="sm"
              >
                {totalItems}
              </Badge>
            )}
          </Button>
        </Box>
      </Flex>

      <SimpleGrid columns={[1, null, 2, 3, 4]} spacing={6} mb={8}>
        {isLoadingProducts ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Box
              key={`skeleton-product-${index}`}
              borderWidth="1px"
              borderRadius="xl"
              overflow="hidden"
              boxShadow="lg"
              bg={cardBgColor}
              p={5}
            >
              <Skeleton height="220px" mb={4} />
              <SkeletonText mt="4" noOfLines={2} spacing="4" mb={4} />
              <Skeleton height="40px" />
            </Box>
          ))
        ) : products.length === 0 ? (
          <Text color={textColorPrimary}>Ürün bulunamadı.</Text>
        ) : (
          products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onOpenModal={handleOpenProductModal}
              cartItems={cart}
              cardBgColor={cardBgColor}
              borderColor={borderColor}
              textColorPrimary={textColorPrimary}
              priceColor={priceColor}
              primaryActionScheme={primaryActionScheme}
              highlightColorScheme={highlightColorScheme}
            />
          ))
        )}
      </SimpleGrid>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size={['full', 'md']}>
        <DrawerOverlay />
        <DrawerContent bg={bgColor}>
          <DrawerCloseButton color={textColorPrimary} />
          <DrawerHeader
            borderBottomWidth="1px"
            bg={useColorModeValue(`${cartButtonScheme}.50`, cardBgColor)}
            color={useColorModeValue(`${cartButtonScheme}.700`, textColorPrimary)}
            borderBottomColor={useColorModeValue(`${cartButtonScheme}.200`, priceColor)}
          >
            <Flex align="center" gap={2}>
              <Icon as={FaShoppingCart} />
              Sepetim ({totalItems} ürün)
            </Flex>
          </DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={4}>
              {cart.length === 0 ? (
                <ScaleFade initialScale={0.9} in>
                  <Flex direction="column" align="center" justify="center" py={12} opacity={0.7}>
                    <Icon as={FaShoppingCart} fontSize="6xl" mb={4} color={textColorSecondary} />
                    <Text fontSize="lg" color={textColorSecondary}>
                      Sepetiniz boş
                    </Text>
                    <Text fontSize="sm" color={textColorSecondary}>
                      Ürün eklemek için sepete ekle butonunu kullanın.
                    </Text>
                  </Flex>
                </ScaleFade>
              ) : (
                cart.map((item) => (
                  <CartItemComponent
                    key={`${item.id}-${item.selectedVariantId}`}
                    item={item}
                    onUpdateQuantity={updateCartItemQuantity}
                    onRemove={removeFromCart}
                    cardBgColor={cardBgColor}
                    borderColor={borderColor}
                    textColorPrimary={textColorPrimary}
                    textColorSecondary={textColorSecondary}
                    priceColor={priceColor}
                    primaryActionScheme={primaryActionScheme}
                  />
                ))
              )}
            </VStack>

            {cart.length > 0 && (
              <Box mt={8}>
                <Box bg={subtleColor} borderRadius="lg" p={4} mb={6}>
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontWeight="medium" color={textColorPrimary}>
                      Ara Toplam:
                    </Text>
                    <Text fontWeight="bold" color={textColorPrimary}>
                      {total.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                    </Text>
                  </Flex>
                  <Divider my={2} />
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontWeight="medium" color={textColorPrimary}>
                      Kargo:
                    </Text>
                    <HStack spacing={2}>
                      <Text fontWeight="bold" color={useColorModeValue('green.500', 'green.300')}>
                        Ücretsiz
                      </Text>
                      <Text fontWeight="bold" color={textColorSecondary} textDecoration="line-through">
                        {Number(99.99).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                      </Text>
                    </HStack>
                  </Flex>
                  <Divider my={2} />
                  <Flex justify="space-between" align="center" mb={3}>
                    <Text fontWeight="bold" fontSize="lg" color={textColorPrimary}>
                      Toplam:
                    </Text>
                    <Text
                      fontWeight="bold"
                      fontSize="xl"
                      color={useColorModeValue(`${primaryActionScheme}.600`, priceColor)}
                    >
                      {total.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                    </Text>
                  </Flex>
                  <Box mb={2}>
                    <Flex justify="space-between" alignItems="center" mb={1}>
                      <Text fontSize="sm" color={textColorSecondary}>
                        Mevcut Bakiye:
                      </Text>
                      {isLoadingBalance ? (
                        <Skeleton height="20px" width="80px" />
                      ) : (
                        <Text fontWeight="semibold" color={textColorPrimary}>
                          {currentBalance.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                        </Text>
                      )}
                    </Flex>
                    <Flex justify="space-between" alignItems="center" mb={1}>
                      <Text fontSize="sm" color={textColorSecondary}>
                        Kalan Bakiye:
                      </Text>
                      {isLoadingBalance ? (
                        <Skeleton height="20px" width="80px" />
                      ) : (
                        <Text
                          fontWeight="semibold"
                          color={
                            balanceAfterPurchase < 0
                              ? useColorModeValue('red.500', 'red.300')
                              : useColorModeValue('green.500', 'green.300')
                          }
                        >
                          {balanceAfterPurchase.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                        </Text>
                      )}
                    </Flex>
                    <Tooltip label="Alışveriş sonrası mevcut bakiyeden kalan tutar" placement="top" hasArrow>
                      <Box>
                        <Progress
                          value={isLoadingBalance ? 0 : balancePercentage}
                          colorScheme={balanceAfterPurchase < 0 ? 'red' : 'green'}
                          size="sm"
                          borderRadius="md"
                        />
                      </Box>
                    </Tooltip>
                  </Box>
                  {currentBalance !== null && balanceAfterPurchase < 0 && !isLoadingBalance && (
                    <Flex
                      bg={useColorModeValue('red.50', 'red.900')}
                      color={useColorModeValue('red.600', 'red.300')}
                      p={3}
                      borderRadius="md"
                      fontSize="sm"
                      align="center"
                      borderWidth="1px"
                      borderColor={useColorModeValue('red.200', 'red.700')}
                    >
                      <Icon as={FaInfoCircle} mr={2} boxSize={5} />
                      <Text fontWeight="medium">Yetersiz bakiye. Lütfen bakiye yükleyiniz.</Text>
                    </Flex>
                  )}
                </Box>
                <Divider my={6} borderColor={borderColor} />
                <Box>
                  <Text
                    fontWeight="bold"
                    mb={4}
                    fontSize="lg"
                    color={useColorModeValue(`${subtleColor}.700`, `${subtleColor}.700`)}
                  >
                    Teslimat Bilgileri
                  </Text>
                  <VStack spacing={4} align="stretch">
                    <FormControlFormInput
                      label="Ad Soyad"
                      placeholder="Ad Soyad"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      isDisabled={isLoadingCheckoutInfo}
                      error={!!formik.errors.name}
                      helperText={formik.errors.name}
                      isRequired
                    />
                    <FormControlFormPhoneInput
                      label="Telefon"
                      placeholder="Telefon"
                      name="phone"
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      isDisabled={isLoadingCheckoutInfo}
                      type="tel"
                      error={!!formik.errors.phone}
                      helperText={formik.errors.phone}
                      isRequired
                    />
                    <CitySelect
                      name="cityId"
                      value={formik.values.cityId}
                      onChange={(e: any) => {
                        formik.setFieldValue('cityId', e.target.value);
                        formik.setFieldValue('countyId', '');
                      }}
                      isDisabled={isLoadingCheckoutInfo}
                      error={!!formik.errors.cityId}
                      helperText={formik.errors.cityId}
                      isRequired
                    />
                    <CountySelect
                      name="countyId"
                      cityId={formik.values.cityId ? parseInt(formik.values.cityId, 10) : undefined}
                      value={formik.values.countyId}
                      onChange={(e: any) => formik.setFieldValue('countyId', e.target.value)}
                      isDisabled={isLoadingCheckoutInfo || !formik.values.cityId}
                      error={!!formik.errors.countyId}
                      helperText={formik.errors.countyId}
                      isRequired
                    />
                    <FormControlFormInput
                      label="Adres"
                      placeholder="Adres"
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      isDisabled={isLoadingCheckoutInfo}
                      error={!!formik.errors.address}
                      helperText={formik.errors.address}
                      isRequired
                    />
                  </VStack>
                </Box>
              </Box>
            )}
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px" borderTopColor={borderColor}>
            <Button
              variant="outline"
              mr={3}
              onClick={onClose}
              borderColor={textColorSecondary}
              color={textColorSecondary}
              _hover={{ bg: useColorModeValue('blackAlpha.50', 'whiteAlpha.50') }}
              isDisabled={isSubmitting}
            >
              Kapat
            </Button>
            <Button
              colorScheme={primaryActionScheme}
              onClick={handleCheckout}
              isDisabled={
                cart.length === 0 ||
                currentBalance === null ||
                total > currentBalance ||
                isLoadingBalance ||
                isSubmitting
              }
              leftIcon={<Icon as={FaCheck} />}
              size="lg"
              isLoading={isSubmitting}
              loadingText="Sipariş Oluşturuluyor..."
            >
              Alışverişi Tamamla
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <ProductModal
        isOpen={isProductModalOpen && !!selectedProduct}
        onClose={onProductModalClose}
        product={selectedProduct}
        selectedVariantId={selectedVariantId}
        setSelectedVariantId={setSelectedVariantId}
        modalQuantity={modalQuantity}
        setModalQuantity={setModalQuantity}
        onAddToCart={handleAddToCartFromModal}
        cardBgColor={cardBgColor}
        textColorPrimary={textColorPrimary}
        priceColor={priceColor}
        borderColor={borderColor}
        highlightColorScheme={highlightColorScheme}
        bgColor={bgColor}
      />
    </Box>
  );
};
